import React, { useState, useEffect, useRef, useMemo, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Clock, 
  LayoutDashboard, Users, Calendar, FileText, DollarSign, BarChart3, 
  Settings, LogOut, Menu, X, Bell, ChevronDown, ChevronRight,
  Home, CreditCard, Stethoscope, Mail, PieChart, Shield, Wrench, HelpCircle,
  UserPlus, ClipboardList, MessageSquare, Activity, Search, User, Phone, MapPin, Brain,
  Lock, Key, BookOpen, Moon, Sun, Loader2, Link2, AlertTriangle, Upload, Sparkles, FileSignature
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import SessionTimeoutWarning from './SessionTimeoutWarning';
import HIPAAAcknowledgmentModal from './HIPAAAcknowledgmentModal';
import LanguageSwitcher from './LanguageSwitcher';
import DocumentLanguageHelper from './DocumentLanguageHelper';
import BackButton from './BackButton';
import GlobalUploadModal from './GlobalUploadModal';
import { DashboardSEO } from './SEOHead';
import { useHIPAACompliance } from '../hooks/useHIPAACompliance';
import { useTranslation } from '../context/TranslationContext';
import api from '../api';

// Dark Mode Context
const DarkModeContext = createContext();
export const useDarkMode = () => useContext(DarkModeContext);

// Service Navigation HIPAA Modal
const ServiceNavigationModal = ({ isOpen, onAccept, onDecline, targetService }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  
  useEffect(() => {
    if (isOpen) setAcknowledged(false);
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]" data-testid="service-hipaa-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Service Access Authorization</h3>
              <p className="text-orange-100 text-sm">HIPAA Compliance Required</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              You are about to access <strong>{targetService}</strong>. This area may contain Protected Health Information (PHI).
            </p>
          </div>
          
          <p className="text-gray-600 text-sm mb-4">
            By proceeding, you acknowledge that:
          </p>
          
          <ul className="text-sm text-gray-600 space-y-2 mb-4">
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-1">•</span>
              You will only access information necessary for your job duties
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-1">•</span>
              All access is logged and audited for compliance
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-1">•</span>
              Unauthorized access may result in penalties
            </li>
          </ul>
          
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <input 
              type="checkbox" 
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
              data-testid="service-hipaa-checkbox"
            />
            <span className="text-sm text-gray-700 font-medium">
              I understand and agree to comply with HIPAA regulations
            </span>
          </label>
        </div>
        
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <button 
            onClick={onDecline}
            className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium"
            data-testid="service-hipaa-cancel"
          >
            Cancel
          </button>
          <button 
            onClick={onAccept}
            disabled={!acknowledged}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              acknowledged 
                ? 'bg-orange-500 text-white hover:bg-orange-600' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            data-testid="service-hipaa-accept"
          >
            Proceed to {targetService}
          </button>
        </div>
      </div>
    </div>
  );
};

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['patient']);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [allPatients, setAllPatients] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showIHDropdown, setShowIHDropdown] = useState(false);
  const [serviceNavModal, setServiceNavModal] = useState({ open: false, target: '', href: '' });
  const [showGlobalUploadModal, setShowGlobalUploadModal] = useState(false);
  const [recentAutoPopulate, setRecentAutoPopulate] = useState(null);
  const searchRef = useRef(null);
  const ihDropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Translation Context
  const { t, isTranslating, currentLanguage } = useTranslation();
  
  // Global Dark Mode State
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('ivoryDarkMode');
    return saved === 'true';
  });

  // Load recent auto-populate data from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentAutoPopulate');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        // Only show indicator if less than 24 hours old
        const timestamp = new Date(data.timestamp);
        const now = new Date();
        if ((now - timestamp) < 24 * 60 * 60 * 1000) {
          setRecentAutoPopulate(data);
        } else {
          localStorage.removeItem('recentAutoPopulate');
        }
      } catch (e) {
        console.error('Error parsing recentAutoPopulate:', e);
      }
    }
    
    // Listen for auto-populate events
    const handleAutoPopulate = (event) => {
      setRecentAutoPopulate(event.detail);
    };
    window.addEventListener('patientAutoPopulated', handleAutoPopulate);
    return () => window.removeEventListener('patientAutoPopulated', handleAutoPopulate);
  }, []);

  // Handle service button click with HIPAA check
  const handleServiceClick = (e, serviceName, href) => {
    e.preventDefault();
    
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Show HIPAA modal
    setServiceNavModal({ open: true, target: serviceName, href });
  };
  
  const handleServiceNavAccept = () => {
    const href = serviceNavModal.href;
    setServiceNavModal({ open: false, target: '', href: '' });
    navigate(href);
  };
  
  const handleServiceNavDecline = () => {
    setServiceNavModal({ open: false, target: '', href: '' });
  };

  // Persist dark mode preference
  useEffect(() => {
    localStorage.setItem('ivoryDarkMode', darkMode.toString());
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // HIPAA Compliance - Show modal ONCE on first dashboard load
  // Check initial state from localStorage
  const getInitialHipaaState = () => {
    const sessionAck = sessionStorage.getItem('hipaaAcknowledged');
    if (sessionAck === 'true') return false;
    // Show HIPAA modal for new sessions - required for healthcare compliance
    return true;
  };
  
  const [showHIPAAModal, setShowHIPAAModal] = useState(getInitialHipaaState);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const acknowledgedPagesRef = useRef(new Set());
  const initialLoadRef = useRef(true);

  // HIPAA Compliance
  const {
    sessionStatus,
    hipaaStatus,
    setHipaaStatus,
    extendSession,
    logout,
    acknowledgeHIPAA
  } = useHIPAACompliance();

  // Show HIPAA modal on initial dashboard load (only if not acknowledged)
  useEffect(() => {
    // Skip if not initial load
    if (!initialLoadRef.current) return;
    
    // First check sessionStorage (for this session)
    const sessionAcknowledged = sessionStorage.getItem('hipaaAcknowledged');
    if (sessionAcknowledged === 'true') {
      initialLoadRef.current = false;
      acknowledgedPagesRef.current.add(location.pathname);
      return;
    }
    
    // Check localStorage user data for HIPAA status
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        if (userData.hipaa?.acknowledged === true) {
          // User already acknowledged in database
          sessionStorage.setItem('hipaaAcknowledged', 'true');
          initialLoadRef.current = false;
          acknowledgedPagesRef.current.add(location.pathname);
          return;
        }
      }
    } catch (e) {
      console.error('Error checking HIPAA status:', e);
    }
    
    // Also check hipaaStatus from hook
    if (hipaaStatus?.acknowledged === true) {
      sessionStorage.setItem('hipaaAcknowledged', 'true');
      initialLoadRef.current = false;
      acknowledgedPagesRef.current.add(location.pathname);
      return;
    }
    
    // User hasn't acknowledged, show modal once
    initialLoadRef.current = false;
    setShowHIPAAModal(true);
  }, [location.pathname, hipaaStatus]);

  // Handle HIPAA acknowledgment
  const handleHIPAAAcknowledge = async () => {
    // Mark session as acknowledged
    sessionStorage.setItem('hipaaAcknowledged', 'true');
    
    // Add current page to acknowledged set
    const pathToAcknowledge = pendingNavigation || location.pathname;
    acknowledgedPagesRef.current.add(pathToAcknowledge);
    
    // Close modal
    setShowHIPAAModal(false);
    
    // Call backend acknowledgment
    await acknowledgeHIPAA();
    
    // Navigate if there was a pending navigation
    if (pendingNavigation) {
      const targetPath = pendingNavigation;
      setPendingNavigation(null);
      navigate(targetPath);
    }
  };

  // Intercept navigation clicks - no modal needed after initial acknowledgment
  const handleNavigationClick = (href, e) => {
    e.preventDefault();
    // Navigate directly since HIPAA was acknowledged on login
    navigate(href);
  };

  // Check for HIPAA info from login response
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        if (userData.hipaa) {
          setHipaaStatus(prev => ({
            ...prev,
            acknowledged: userData.hipaa.acknowledged,
            requiresAcknowledgment: userData.hipaa.requiresAcknowledgment,
            passwordExpired: userData.hipaa.passwordExpired,
            passwordExpiresInDays: userData.hipaa.passwordExpiresInDays,
            twoFactorEnabled: userData.hipaa.twoFactorEnabled
          }));
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, [setHipaaStatus]);

  // Handle session expired event
  useEffect(() => {
    const handleSessionExpired = () => {
      navigate('/login');
    };
    window.addEventListener('sessionExpired', handleSessionExpired);
    return () => window.removeEventListener('sessionExpired', handleSessionExpired);
  }, [navigate]);

  // Fetch all patients on mount for quick search
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const response = await api.get('/patients');
        if (response.data.success) {
          setAllPatients(response.data.patients);
        }
      } catch (error) {
        console.error('Error fetching patients:', error);
      }
    };
    fetchPatients();
  }, []);

  // Filter patients based on search query using useMemo
  const searchResults = useMemo(() => {
    if (searchQuery.trim().length < 2) return [];
    
    const query = searchQuery.toLowerCase();
    return allPatients.filter(patient => 
      `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(query) ||
      patient.phone?.includes(query) ||
      patient.primaryDiagnosis?.toLowerCase().includes(query)
    ).slice(0, 5);
  }, [searchQuery, allPatients]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
        setSelectedIndex(-1);
      }
      if (ihDropdownRef.current && !ihDropdownRef.current.contains(event.target)) {
        setShowIHDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePatientSelect = (patient) => {
    setSearchQuery('');
    setShowSearchDropdown(false);
    setSelectedIndex(-1);
    navigate(`/dashboard/patients/demographics?patientId=${patient.id}`);
  };

  // Keyboard navigation for search
  const handleKeyDown = (e) => {
    if (!showSearchDropdown || searchResults.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handlePatientSelect(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSearchDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const navigation = [
    { name: 'Home', href: '/dashboard/quick-links', icon: Home },
    { name: 'Quick Links', href: '/dashboard', icon: Link2 },
    { name: 'Dashboard', href: '/dashboard/main', icon: LayoutDashboard },
    { name: 'Time Clock', href: '/dashboard/time-clock', icon: Clock },
    { name: 'Staff Hub', href: '/dashboard/staff-hub/weekly_timesheet', icon: FileText },
    { name: 'IvoryMail', href: '/dashboard/ivorymail', icon: Mail },
    { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart3 },
    { name: 'Billing Dashboard', href: '/dashboard/billing-dashboard', icon: CreditCard },
    { 
      name: 'AI Document Generator', 
      icon: Brain,
      id: 'ai-document',
      children: [
        { name: 'Document Generator', href: '/dashboard/document-generator' },
        { name: 'Documentation', href: '/dashboard/documentation' },
      ]
    },
    { 
      name: 'Patient', 
      icon: Users,
      id: 'patient',
      children: [
        { name: 'Patient Demographics', href: '/dashboard/patients/demographics' },
        { name: 'Admission, Insurance & Prior Auth', href: '/dashboard/patients/admission' },
        { name: 'Fillable Admission Packet (PDF)', href: '/documents/Fillable_Admission_Packet_HHA.pdf', external: true },
        { name: 'Admission Consent (IVORY form)', href: '/dashboard/forms/exact/admission_consent_exact' },
        { name: 'Admission Packet HHA (IVORY form)', href: '/dashboard/forms/exact/admission_packet_exact' },
        { name: 'Medication List Template (PDF)', href: '/documents/medication_list_template.pdf', external: true },
        { name: 'Patient Chart', href: '/dashboard/patients/chart' },
        { name: 'Patient Communication Log', href: '/dashboard/patients/communication' },
      ]
    },
    { name: 'COVID-19 Screening', href: '/dashboard/covid-screening', icon: Shield },
    { name: 'Scheduling', href: '/dashboard/scheduling', icon: Calendar },
    { 
      name: 'Clinical', 
      icon: Stethoscope,
      id: 'clinical',
      children: [
        { name: 'Skilled Nursing', id: 'skilled-nursing-sub', isSubMenu: true, children: [
          { name: 'Daily Nursing Notes', href: '/dashboard/skilled-nursing/nurses-notes' },
          { name: 'Wound Care Flow Sheet', href: '/dashboard/skilled-nursing/wound-care' },
          { name: 'IV Therapy Administration', href: '/dashboard/skilled-nursing/iv-therapy' },
          { name: 'PCA Documentation', href: '/dashboard/skilled-nursing/pca' },
        ]},
        { name: 'OASIS-E2 Assessments (SOC/ROC/FU/DC)', href: '/dashboard/forms/oasis' },
        { name: 'Non-OASIS SOC Assessment', href: '/dashboard/forms/non-oasis' },
        { name: 'Sign OASIS Assessments', href: '/dashboard/forms/oasis-e2/sign-list' },
        { name: 'SOC/ROC Comprehensive Assessment', href: '/dashboard/forms/soc-roc' },
        { name: 'Discharge Comprehensive Assessment', href: '/dashboard/forms/discharge-v2' },
        { name: 'Recertification Assessment', href: '/dashboard/forms/recertification' },
        { name: '485 - Certification and Plan of Care', href: '/dashboard/clinical/poc-485' },
        { name: 'Plan of Care Plus', href: '/dashboard/clinical/poc-plus' },
        { name: 'Orders & Documents', href: '/dashboard/clinical/orders' },
        { name: 'Order Plus (Verbal Order Plus)', href: '/dashboard/clinical/order-plus' },
        { name: 'Medication Profile', href: '/dashboard/clinical/medication-profile' },
        { name: 'Medication Administration Log', href: '/dashboard/clinical/medication-log' },
        { name: 'Physical Therapy Documents', href: '/dashboard/clinical/pt-documents' },
        { name: 'Occupational Therapy Documents', href: '/dashboard/clinical/ot-documents' },
        { name: 'Speech Therapy Documents', href: '/dashboard/clinical/st-documents' },
        { name: 'Medical Social Worker Documents', href: '/dashboard/clinical/msw-documents' },
        { name: 'Aide Documents', href: '/dashboard/clinical/aide-documents' },
        { name: 'Non-Skilled Assessment', href: '/dashboard/clinical/non-skilled' },
        { name: 'Electronic Health Records', href: '/dashboard/clinical/ehr' },
        { name: 'Braden Scale', href: '/dashboard/clinical/braden-scale' },
        { name: 'Supervisory Visit Notes', href: '/dashboard/clinical/supervisory-notes' },
        { name: 'Incident Report', href: '/dashboard/clinical/incident-report' },
      ]
    },
    { 
      name: 'Admission & Consent', 
      icon: ClipboardList,
      id: 'admission-consent',
      children: [
        { name: 'Admission Packet', href: '/dashboard/admission/index' },
        { name: 'Emergency Information', href: '/dashboard/admission/emergency-info' },
        { name: 'Consent & Verification', href: '/dashboard/admission/consent-verification' },
        { name: 'Billing Agreement', href: '/dashboard/admission/billing-agreement' },
        { name: 'Home Assessment', href: '/dashboard/admission/home-assessment' },
        { name: 'CMS-485 Template', href: '/dashboard/templates/cms-485' },
        { name: 'Patient Face Sheet', href: '/dashboard/templates/face-sheet' },
      ]
    },
    { 
      name: 'Behavioral Health', 
      icon: Brain,
      id: 'behavioral-health',
      children: [
        { name: 'Dashboard', href: '/dashboard/behavioral-health' },
        { name: 'Telehealth Sessions', href: '/dashboard/behavioral-health/telehealth' },
        { name: 'Clients', href: '/dashboard/behavioral-health/clients' },
        { name: 'Session Notes', href: '/dashboard/behavioral-health/notes' },
        { name: 'Secure Messages', href: '/dashboard/behavioral-health/messages' },
        { name: 'Schedule', href: '/dashboard/behavioral-health/schedule' },
      ]
    },
    { 
      name: 'Billing', 
      icon: DollarSign,
      id: 'billing',
      children: [
        { name: 'Claims', href: '/dashboard/billing/claims' },
        { name: 'RAP Due', href: '/dashboard/billing/rap' },
        { name: 'NOA Due', href: '/dashboard/billing/noa' },
        { name: 'Claim Submission', href: '/dashboard/billing/submission' },
        { name: 'Payment Posting', href: '/dashboard/billing/payment-posting' },
      ]
    },
    { 
      name: 'A/R', 
      icon: PieChart,
      id: 'ar',
      children: [
        { name: 'Accounts Receivable', href: '/dashboard/ar' },
        { name: 'Aging Report', href: '/dashboard/ar/aging' },
        { name: 'Collections', href: '/dashboard/ar/collections' },
      ]
    },
    { name: 'QA Center', href: '/dashboard/qa', icon: Shield },
    { name: 'Order Signatures', href: '/dashboard/order-signatures', icon: FileSignature },
    { name: 'Tutorials', href: '/dashboard/tutorials', icon: BookOpen },
    { name: 'QAPI', href: '/dashboard/qapi', icon: Activity },
    { name: 'Policy & Procedure', href: '/dashboard/policy-procedure', icon: BookOpen },
    { name: 'HIPAA Compliance', href: '/dashboard/hipaa', icon: Lock },
    { name: 'Security Settings', href: '/dashboard/security-settings', icon: Key },
    { 
      name: 'Setup', 
      icon: Settings,
      id: 'setup',
      children: [
        { name: 'Aide Service Titles', href: '/dashboard/setup/aide-service-titles' },
        { name: 'A/R Adjustment Reason', href: '/dashboard/setup/ar-adjustment-reason' },
        { name: 'Aide Task Codes', href: '/dashboard/setup/aide-task-codes' },
        { name: 'Billing Code', href: '/dashboard/setup/billing-code' },
        { name: 'Staff (Caregiver)', href: '/dashboard/setup/staff' },
        { name: 'Care Plan Templates', href: '/dashboard/setup/care-plan-templates' },
        { name: 'Communication Type for Staff', href: '/dashboard/setup/communication-type-staff' },
        { name: 'Electronic Staff (Caregiver) Document Folder', href: '/dashboard/setup/electronic-staff-folder' },
        { name: 'CBSA Codes', href: '/dashboard/setup/cbsa-codes' },
        { name: 'Discipline', href: '/dashboard/setup/discipline' },
        { name: 'Emergency Preparedness Contacts', href: '/dashboard/setup/emergency-contacts' },
        { name: 'Evacuation/Emergency Preparedness Level', href: '/dashboard/setup/evacuation-level' },
        { name: 'EVV Change Reasons', href: '/dashboard/setup/evv-change-reasons' },
        { name: 'General Ledger', href: '/dashboard/setup/general-ledger' },
        { name: 'Insurance Case Manager', href: '/dashboard/setup/insurance-case-manager' },
        { name: 'Missed Visit Reason', href: '/dashboard/setup/missed-visit-reason' },
        { name: 'Office', href: '/dashboard/setup/office' },
        { name: 'Patient Communication Type', href: '/dashboard/setup/patient-communication-type' },
        { name: 'Patient Electronic Document Folder', href: '/dashboard/setup/patient-electronic-folder' },
        { name: 'Payer', href: '/dashboard/setup/payer' },
        { name: 'Patient Facilities', href: '/dashboard/setup/patient-facilities' },
        { name: 'Phrases', href: '/dashboard/setup/phrases' },
        { name: 'Pay Rate', href: '/dashboard/setup/pay-rate' },
        { name: 'Physician', href: '/dashboard/setup/physician' },
        { name: 'Reason Not Admitted', href: '/dashboard/setup/reason-not-admitted' },
        { name: 'Referral Source', href: '/dashboard/setup/referral-source' },
        { name: 'Region', href: '/dashboard/setup/region' },
        { name: 'Supply Code', href: '/dashboard/setup/supply-code' },
        { name: 'Add-on Pay Rate', href: '/dashboard/setup/addon-pay-rate' },
        { name: 'Incident Type', href: '/dashboard/setup/incident-type' },
        { name: 'Earnings Codes', href: '/dashboard/setup/earnings-codes' },
      ]
    },
    { name: 'Reports', href: '/dashboard/reports', icon: BarChart3 },
    { 
      name: 'Inovalon Claim Portal', 
      icon: FileText,
      id: 'inovalon',
      children: [
        { name: 'Claims Management Pro', href: '/dashboard/inovalon/claims-pro' },
        { name: 'Claims Management Medicare Pro', href: '/dashboard/inovalon/claims-medicare-pro' },
        { name: 'Claims Management', href: '/dashboard/inovalon/claims-management' },
        { name: 'All Payer Eligibility Workflow', href: '/dashboard/inovalon/eligibility-workflow' },
      ]
    },
    { 
      name: 'Admin', 
      icon: UserPlus,
      id: 'admin',
      children: [
        { name: 'Customize', href: '/dashboard/admin/customize' },
        { name: 'User / Security Manager', href: '/dashboard/admin/users' },
        { name: 'Features Activation', href: '/dashboard/admin/features' },
        { name: 'Account & Invoices', href: '/dashboard/admin/billing' },
        { name: 'Translation Memory', href: '/dashboard/admin/translation-memory' },
        { name: 'Staff Management', href: '/dashboard/admin/staff' },
        { name: 'Credentials', href: '/dashboard/admin/credentials' },
        { name: 'Audit Log', href: '/dashboard/admin/audit' },
      ]
    },
    { 
      name: 'Tools', 
      icon: Wrench,
      id: 'tools',
      children: [
        { name: 'Notification Manager', href: '/dashboard/tools/notification-manager' },
        { name: 'Travel Time Calculator', href: '/dashboard/tools/travel-time-calculator' },
        { name: 'Electronic Remittance Posting', href: '/dashboard/tools/electronic-remittance' },
        { name: 'OASIS Segue', href: '/dashboard/tools/oasis-segue' },
        { name: 'CAHPS Export', href: '/dashboard/tools/cahps-export' },
        { name: 'Fax Confirmation', href: '/dashboard/tools/fax-confirmation' },
        { name: 'Document Library', href: '/dashboard/tools/document-library' },
        { name: 'Undelete Documents', href: '/dashboard/tools/undelete-documents' },
        { name: 'Offline Conflicts Center', href: '/dashboard/tools/offline-conflicts' },
        { name: 'Data Import', href: '/dashboard/tools/data-import' },
        { name: 'Oasis Export', href: '/dashboard/tools/oasis-export' },
        { name: 'Signature Audit Trail', href: '/dashboard/tools/signature-audit' },
        { name: '✨ Document Conversion (AI)', href: '/dashboard/tools/document-conversion' },
      ]
    },
    { 
      name: 'Help & Training', 
      icon: HelpCircle,
      id: 'help',
      children: [
        { name: 'FAQ', href: '/dashboard/faq' },
        { name: 'Features Activation', href: '/dashboard/features-activation' },
        { name: 'Live Training', href: '/dashboard/live-training' },
        { name: 'Training Videos', href: '/dashboard/training-videos' },
      ]
    },
    { 
      name: 'Ivory Clinic', 
      icon: Stethoscope,
      id: 'clinic',
      children: [
        { name: 'Desktop', href: '/dashboard/clinic' },
        { name: 'Appointments', href: '/dashboard/clinic/appointments' },
        { name: 'Patient Visits', href: '/dashboard/clinic/patient-visits' },
        { name: 'Claims/Billing', href: '/dashboard/clinic/claims' },
        { name: 'Accounting', href: '/dashboard/clinic/accounting' },
        { name: 'Manage Patients', href: '/dashboard/clinic/patients' },
        { name: 'Patient Portal', href: '/dashboard/clinic/portal' },
        { name: 'Manage Office', href: '/dashboard/clinic/office' },
      ]
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const isActive = (href) => location.pathname === href;
  const isParentActive = (children) => children?.some(child => location.pathname === child.href);

  return (
    <DarkModeContext.Provider value={{ darkMode, setDarkMode }}>
    <div className={`min-h-screen ${darkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
      {/* SEO Head */}
      <DashboardSEO />
      
      {/* HIPAA Compliance Modals */}
      <SessionTimeoutWarning
        remainingMinutes={sessionStatus.remainingMinutes}
        onExtendSession={extendSession}
        onLogout={logout}
        show={sessionStatus.showWarning}
      />
      
      {/* HIPAA Acknowledgment Modal - Shows on every page navigation */}
      <HIPAAAcknowledgmentModal
        onAcknowledge={handleHIPAAAcknowledge}
        show={showHIPAAModal}
      />

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#2c3e50] text-white z-50 transform transition-transform duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        {/* Logo */}
        <div className="p-4 border-b border-[#34495e]">
          <Link to="/" className="flex items-center space-x-3">
            <div className="h-16 w-16 rounded-full overflow-hidden bg-gradient-to-br from-[#d4af37] via-[#f5d778] to-[#b8860b] p-1 shadow-xl ring-2 ring-[#f39c12]/30">
              <img src="/images/ivory-logo.png" alt="Ivory Medical Home" className="h-full w-full object-cover rounded-full ivory-logo-spin" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-[#f39c12]">IVORY</span>
              <span className="text-xs text-gray-300">Medical Home</span>
            </div>
          </Link>
        </div>

        {/* Global Upload Button */}
        <div className="px-3 py-2 border-b border-[#34495e]">
          <button
            onClick={() => setShowGlobalUploadModal(true)}
            className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded text-xs transition-all"
            data-testid="global-upload-btn"
          >
            <Upload className="w-3 h-3" />
            <span>Upload & Auto-Populate</span>
            <Sparkles className="w-3 h-3 text-gray-300" />
          </button>
          <Link to="/dashboard/upload-hub" className="mt-1 w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-[#d4af37] hover:bg-[#c9a227] text-gray-900 font-semibold rounded text-xs" data-testid="upload-hub-link">
            <Upload className="w-3 h-3" /><span>Upload Hub: timesheets, wounds, meds, cards…</span>
          </Link>
          {recentAutoPopulate && (
            <div className="mt-2 p-2 bg-green-900/30 rounded-lg border border-green-700/50">
              <p className="text-xs text-green-300 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Recently auto-populated: 
                <Link 
                  to={`/dashboard/auto-populate-summary/${recentAutoPopulate.patientId}`}
                  className="font-medium text-green-200 hover:underline"
                >
                  {recentAutoPopulate.patientName}
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-2 overflow-y-auto space-y-2" style={{ height: 'calc(100vh - 220px)' }}>
          {navigation.map((item) => (
            <div key={item.name} className="mb-2">
              {item.children ? (
                // Expandable menu
                <div>
                  <button
                    onClick={() => toggleMenu(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-150 transform hover:translate-y-[-2px] active:translate-y-[1px] ${
                      isParentActive(item.children)
                        ? 'bg-gradient-to-b from-[#f5a623] to-[#e08e0b] text-white shadow-[0_4px_0_#b8720a,0_6px_10px_rgba(0,0,0,0.3)] border-t border-[#ffc04d]'
                        : 'bg-gradient-to-b from-[#4a5568] to-[#2d3748] text-gray-200 shadow-[0_4px_0_#1a202c,0_6px_10px_rgba(0,0,0,0.25)] border-t border-[#606a7a] hover:from-[#5a6578] hover:to-[#3d4758]'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </div>
                    {expandedMenus.includes(item.id) ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  {expandedMenus.includes(item.id) && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-[#34495e] pl-3">
                      {item.children.map((child) => (
                        child.isSubMenu ? (
                          // Nested submenu (e.g., Skilled Nursing)
                          <div key={child.id}>
                            <button
                              onClick={() => toggleMenu(child.id)}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-150 transform hover:translate-y-[-2px] active:translate-y-[1px] ${
                                child.children?.some(subChild => isActive(subChild.href))
                                  ? 'bg-gradient-to-b from-[#e8943a] to-[#c76b15] text-white shadow-[0_3px_0_#9a5310,0_5px_8px_rgba(0,0,0,0.3)] border-t border-[#f0a85c]'
                                  : 'bg-gradient-to-b from-[#3d4654] to-[#262d38] text-gray-300 shadow-[0_3px_0_#1a1f28,0_5px_8px_rgba(0,0,0,0.25)] border-t border-[#505868] hover:from-[#4d5664] hover:to-[#363d48]'
                              }`}
                            >
                              <span className="font-semibold">{child.name}</span>
                              {expandedMenus.includes(child.id) ? (
                                <ChevronDown className="w-3 h-3" />
                              ) : (
                                <ChevronRight className="w-3 h-3" />
                              )}
                            </button>
                            {expandedMenus.includes(child.id) && (
                              <div className="ml-3 mt-1 space-y-1 border-l border-[#4a5568] pl-2">
                                {child.children.map((subChild) => (
                                  <a
                                    key={subChild.href}
                                    href={subChild.href}
                                    onClick={(e) => handleNavigationClick(subChild.href, e)}
                                    className={`block px-2 py-1.5 rounded text-xs transition-all duration-150 cursor-pointer transform hover:translate-y-[-1px] active:translate-y-[1px] ${
                                      isActive(subChild.href)
                                        ? 'bg-gradient-to-b from-[#f5a623] to-[#e08e0b] text-white shadow-[0_2px_0_#b8720a,0_3px_6px_rgba(0,0,0,0.3)] border-t border-[#ffc04d]'
                                        : 'bg-gradient-to-b from-[#3a414d] to-[#252a33] text-gray-400 shadow-[0_2px_0_#181b20,0_3px_6px_rgba(0,0,0,0.2)] border-t border-[#484f5a] hover:from-[#4a515d] hover:to-[#353a43] hover:text-white'
                                    }`}
                                  >
                                    {subChild.name}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : child.external ? (
                          <a
                            key={child.href}
                            href={child.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-3 py-2 rounded-lg text-sm transition-all duration-150 cursor-pointer transform hover:translate-y-[-2px] active:translate-y-[1px] bg-gradient-to-b from-[#3d4654] to-[#262d38] text-gray-300 shadow-[0_3px_0_#1a1f28,0_5px_8px_rgba(0,0,0,0.25)] border-t border-[#505868] hover:from-[#4d5664] hover:to-[#363d48] hover:text-white"
                          >
                            {child.name} ↗
                          </a>
                        ) : (
                          <a
                            key={child.href}
                            href={child.href}
                            onClick={(e) => handleNavigationClick(child.href, e)}
                            className={`block px-3 py-2 rounded-lg text-sm transition-all duration-150 cursor-pointer transform hover:translate-y-[-2px] active:translate-y-[1px] ${
                              isActive(child.href)
                                ? 'bg-gradient-to-b from-[#f5a623] to-[#e08e0b] text-white shadow-[0_3px_0_#b8720a,0_5px_8px_rgba(0,0,0,0.3)] border-t border-[#ffc04d]'
                                : 'bg-gradient-to-b from-[#3d4654] to-[#262d38] text-gray-300 shadow-[0_3px_0_#1a1f28,0_5px_8px_rgba(0,0,0,0.25)] border-t border-[#505868] hover:from-[#4d5664] hover:to-[#363d48] hover:text-white'
                            }`}
                          >
                            {child.name}
                          </a>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Regular menu item
                <a
                  href={item.href}
                  onClick={(e) => handleNavigationClick(item.href, e)}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 cursor-pointer transform hover:translate-y-[-2px] active:translate-y-[1px] ${
                    isActive(item.href)
                      ? 'bg-gradient-to-b from-[#f5a623] to-[#e08e0b] text-white shadow-[0_4px_0_#b8720a,0_6px_10px_rgba(0,0,0,0.3)] border-t border-[#ffc04d]'
                      : 'bg-gradient-to-b from-[#4a5568] to-[#2d3748] text-gray-200 shadow-[0_4px_0_#1a202c,0_6px_10px_rgba(0,0,0,0.25)] border-t border-[#606a7a] hover:from-[#5a6578] hover:to-[#3d4758]'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </a>
              )}
            </div>
          ))}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#34495e]">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-3 py-2 w-full rounded-lg text-gray-200 transition-all duration-150 transform hover:translate-y-[-2px] active:translate-y-[1px] bg-gradient-to-b from-[#4a5568] to-[#2d3748] shadow-[0_4px_0_#1a202c,0_6px_10px_rgba(0,0,0,0.25)] border-t border-[#606a7a] hover:from-[#5a6578] hover:to-[#3d4758]"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} shadow-sm border-b sticky top-0 z-30`}>
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              {/* Quick Patient Search */}
              <div className="hidden sm:block relative" ref={searchRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search patients by name, phone, diagnosis..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedIndex(-1); // Reset selection on new search
                      if (e.target.value.length >= 2) {
                        setShowSearchDropdown(true);
                      } else {
                        setShowSearchDropdown(false);
                      }
                    }}
                    onFocus={() => searchQuery.length >= 2 && setShowSearchDropdown(true)}
                    onKeyDown={handleKeyDown}
                    className="w-80 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#f39c12] focus:border-transparent"
                    data-testid="patient-search-input"
                  />
                </div>
                
                {/* Search Results Dropdown */}
                {showSearchDropdown && searchQuery.length >= 2 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto" data-testid="patient-search-results">
                    {searchResults.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No patients found matching &ldquo;{searchQuery}&rdquo;
                      </div>
                    ) : (
                      <div className="py-1">
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                          Patients ({searchResults.length}) - Use ↑↓ to navigate, Enter to select
                        </div>
                        {searchResults.map((patient, index) => (
                          <button
                            key={patient.id}
                            onClick={() => handlePatientSelect(patient)}
                            className={`w-full px-3 py-3 flex items-start space-x-3 text-left border-b border-gray-100 last:border-0 transition-colors ${
                              index === selectedIndex 
                                ? 'bg-[#fff0e6] border-l-4 border-l-[#ea580c]' 
                                : 'hover:bg-[#fff8f0]'
                            }`}
                            data-testid={`patient-result-${patient.id}`}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0 ${
                              index === selectedIndex ? 'bg-[#ea580c]' : 'bg-[#3498db]'
                            }`}>
                              {patient.firstName?.[0]}{patient.lastName?.[0]}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="font-medium text-gray-900 truncate">
                                  {patient.firstName} {patient.lastName}
                                </p>
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  patient.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {patient.status}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                {patient.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {patient.phone}
                                  </span>
                                )}
                                {patient.primaryDiagnosis && (
                                  <span className="flex items-center gap-1 truncate">
                                    <Stethoscope className="w-3 h-3" />
                                    {patient.primaryDiagnosis}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                        <div className="px-3 py-2 bg-gray-50 border-t">
                          <button
                            onClick={() => {
                              setShowSearchDropdown(false);
                              navigate('/dashboard/patients');
                            }}
                            className="text-sm text-[#f39c12] hover:text-[#e67e22] font-medium"
                          >
                            View all patients →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Translation Status Indicator */}
              {isTranslating && (
                <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-lg animate-pulse">
                  <Loader2 className="w-3 h-3 text-blue-600 animate-spin" />
                  <span className="text-xs text-blue-700">
                    {t('language.translating', 'Translating...')}
                  </span>
                </div>
              )}
              {/* Dark Mode Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  darkMode 
                    ? 'bg-slate-700 text-yellow-400 hover:bg-slate-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="dark-mode-toggle"
                title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              {/* Language Switcher */}
              <div className="hidden md:flex items-center gap-2">
                <LanguageSwitcher />
              </div>
              {/* Session Timer - HIPAA Compliance */}
              <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-green-50 border border-green-200 rounded-lg">
                <Lock className="w-3 h-3 text-green-600" />
                <span className="text-xs text-green-700">
                  Session: {Math.floor(sessionStatus.remainingMinutes)}m
                </span>
              </div>
              <button className="px-3 py-1 bg-[#3498db] text-white rounded text-sm font-medium">
                {t('common.new', 'NEW')}
              </button>
              <NotificationBell />
              <div className="relative" ref={ihDropdownRef}>
                <button 
                  onClick={() => setShowIHDropdown(!showIHDropdown)}
                  className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                  data-testid="ih-dropdown-btn"
                >
                  <div className="w-8 h-8 bg-[#3498db] rounded-full flex items-center justify-center text-white font-medium">
                    IH
                  </div>
                  <span className="hidden sm:block text-sm text-gray-700">Ivory Health</span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showIHDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showIHDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[70vh] overflow-y-auto">
                    <div className="p-3 border-b border-gray-100 bg-gray-50">
                      <p className="text-sm font-semibold text-gray-800">Quick Navigation</p>
                    </div>
                    {navigation.map((item, index) => (
                      <div key={index}>
                        {item.children ? (
                          <div>
                            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-y border-gray-100">
                              {item.name}
                            </div>
                            {item.children.map((child, childIndex) => (
                              child.children ? (
                                <div key={childIndex}>
                                  <div className="px-6 py-1 text-xs text-gray-400 uppercase">{child.name}</div>
                                  {child.children.map((subChild, subIndex) => (
                                    <Link
                                      key={subIndex}
                                      to={subChild.href}
                                      className="block px-8 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                                      onClick={() => setShowIHDropdown(false)}
                                    >
                                      {subChild.name}
                                    </Link>
                                  ))}
                                </div>
                              ) : (
                                <Link
                                  key={childIndex}
                                  to={child.href}
                                  className="block px-6 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                                  onClick={() => setShowIHDropdown(false)}
                                >
                                  {child.name}
                                </Link>
                              )
                            ))}
                          </div>
                        ) : (
                          <Link
                            to={item.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 border-b border-gray-50"
                            onClick={() => setShowIHDropdown(false)}
                          >
                            {item.name}
                          </Link>
                        )}
                      </div>
                    ))}
                    {/* Logout Option */}
                    <div className="border-t border-gray-200 mt-2">
                      <button
                        onClick={() => {
                          setShowIHDropdown(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        data-testid="ih-dropdown-logout"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Service Navigation HIPAA Modal */}
        <ServiceNavigationModal
          isOpen={serviceNavModal.open}
          onAccept={handleServiceNavAccept}
          onDecline={handleServiceNavDecline}
          targetService={serviceNavModal.target}
        />

        {/* Global Upload Modal */}
        <GlobalUploadModal
          isOpen={showGlobalUploadModal}
          onClose={() => setShowGlobalUploadModal(false)}
        />

        {/* Page content */}
        <main id="main-content" className={`p-6 ${darkMode ? 'bg-slate-900 text-gray-100' : ''}`} role="main" aria-label="Main content">
          {/* Back Button */}
          <BackButton className="mb-4" />
          {/* Show Document Language Helper on clinical/documentation pages */}
          {(location.pathname.includes('/clinical') || 
            location.pathname.includes('/oasis') || 
            location.pathname.includes('/poc') ||
            location.pathname.includes('/documentation')) && (
            <DocumentLanguageHelper showConvertButton={true} />
          )}
          {children}
        </main>
      </div>
    </div>
    </DarkModeContext.Provider>
  );
};

export default DashboardLayout;
